import {
  Button,
  createCssVariableString,
  MatchUp,
  Modal,
  Typography,
  useLoading,
  useIsVisible,
  Section,
} from "best-common-react";
import React, { memo, RefObject, useRef, useState, lazy, Suspense } from "react";
import { bulkRefundGame } from "../../../api/RequesTixApi";
import { GamePreviewDTO } from "../../../types/Game";
import { TicketRequestDTO } from "../../../types/TicketRequest";
import { useTimezone } from "../../../util/TimeUtil";
import EventCanceled from "../misc/EventCanceled";
import GamePreviewDisplay from "../../game/GamePreviewDisplay";
const AdminRequestTable = lazy(() => import("./AdminRequestTable"));

type RefundGameModalProps = {
  show: boolean;
  game: GamePreviewDTO;
  onClose: () => void;
};

const RefundGameModal = ({ show, game, onClose }: RefundGameModalProps) => {
  const { setLoading } = useLoading();

  const refundGame = async () => {
    if (game.gamePk) {
      setLoading(true);
      await bulkRefundGame(game.gamePk);
      onClose();
      setLoading(false);
    }
  };

  return !!game ? (
    <Modal show={show} size="lg">
      <Modal.Header close={onClose}>Are you sure?</Modal.Header>
      <Modal.Body>
        <GamePreviewDisplay game={game} />
        <div className="bcr-mt-3">
          <b>Are you sure you want to bulk refund this game?</b>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          <Button variant="primary" className="bcr-me-2" disabled={!game.gamePk} onClick={() => refundGame()}>
            Refund Game
          </Button>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  ) : null;
};

type AdminRequestsGameProps = {
  game: GamePreviewDTO;
  requests: TicketRequestDTO[];
  showHeader?: boolean;
  showRequestCountInHeader?: boolean;
  startOpen?: boolean;
  jumpToRequestId?: string | number;
};

const AdminRequestsGame = memo(
  ({
    game,
    requests = [],
    showHeader = false,
    showRequestCountInHeader = false,
    startOpen = false,
    jumpToRequestId,
  }: AdminRequestsGameProps) => {
    const [showRefund, setShowRefund] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const isVisible = useIsVisible(ref as RefObject<HTMLElement>, true, 0);
    const tz = game?.venue ? game.venue.timeZone : null;
    const { time, day, date } = useTimezone(game?.gameDate, tz!, game?.status);

    if (!game) return null;

    return (
      <div ref={ref}>
        <Section
          header={
            <div className="d-flex align-items-center w-100 bcr-gap-2">
              <div className="d-flex flex-column bcr-gap-1">
                <div className="d-flex align-items-center flex-wrap bcr-gap-2">
                  <Typography variant="titleMedium" elementStyles={{ weight: "bold" }}>
                    {date}
                  </Typography>
                  <MatchUp
                    home={game.teams.home.team}
                    away={game.teams.away.team}
                    description={game.seriesDescription}
                  />
                </div>
                <div className="d-flex align-items-center flex-wrap bcr-gap-2">
                  <Typography elementStyles={{ color: createCssVariableString("onColorsOnSurfaceLow") }}>
                    {day} {time}
                  </Typography>
                  <EventCanceled game={game} />
                </div>
              </div>
              {showRequestCountInHeader && (
                <div className="bcr-ms-auto ">
                  <Typography
                    variant="bodySmall"
                    style={{
                      fontStyle: "italic",
                      color: createCssVariableString("color-on-surface-low"),
                    }}
                  >
                    {requests.length} {requests.length === 1 ? "Request" : "Requests"}
                  </Typography>
                </div>
              )}
            </div>
          }
          sectionKey={`game-${game.gamePk}`}
          startsOpen={startOpen}
        >
          <div>
            <Button variant="default" onClick={() => setShowRefund(true)}>
              Refund Game
            </Button>
            {showRefund && <RefundGameModal game={game} show={showRefund} onClose={() => setShowRefund(false)} />}
          </div>
          {!!(requests.length > 0 || showHeader) && (
            <div className="bcr-mt-2">
              <Suspense fallback={null}>
                {isVisible && (
                  <AdminRequestTable requests={requests} jumpToRequestId={jumpToRequestId} visible={isVisible} />
                )}
              </Suspense>
            </div>
          )}
        </Section>
      </div>
    );
  },
);

export default AdminRequestsGame;
